// Audit Logging API - unified organization activity log
// Backed by GET /auth/organizations/{org_id}/activity-log (merges
// security_events + audit_log + user_activities on the auth service).

import { authenticatedFetch, API_BASE } from './authenticatedFetch';

export interface ActivityLogEntry {
  id: string;
  timestamp: string;
  category: 'security' | 'activity';
  event_type: string;
  description: string;
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  user_id?: string;
  user_name?: string;
  ip_address?: string;
  user_agent?: string;
  risk_score?: number;
  is_suspicious?: boolean;
  resource_type?: string;
  resource_name?: string;
  action?: string;
  changes?: { old: any; new: any };
  metadata?: object;
}

export interface ActivityLogStats {
  total_events: number;
  security_events: number;
  activity_events: number;
  high_risk_events: number;
  suspicious_events: number;
  events_by_category: { [key: string]: number };
  events_by_severity: { [key: string]: number };
  recent_locations: string[];
}

export interface ActivityLogResponse {
  organization_id: string;
  organization_name: string;
  period: { start: string; end: string };
  stats: ActivityLogStats;
  activities: ActivityLogEntry[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return authenticatedFetch<T>(endpoint, { ...options, serviceName: 'Audit API' });
}

export const auditAPI = {
  async getActivityLog(organizationId: string, params?: {
    limit?: number;
    offset?: number;
    start_date?: string;
    end_date?: string;
    event_types?: string;
    severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
  }): Promise<ActivityLogResponse> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.event_types) queryParams.append('event_types', params.event_types);
    if (params?.severity) queryParams.append('severity', params.severity);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/auth/organizations/${organizationId}/activity-log?${queryString}`
      : `/auth/organizations/${organizationId}/activity-log`;

    return await apiRequest<ActivityLogResponse>(url, { method: 'GET' });
  },

  async exportActivityLog(organizationId: string, params?: {
    start_date?: string;
    end_date?: string;
    event_types?: string;
    severity?: 'info' | 'low' | 'medium' | 'high' | 'critical';
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.event_types) queryParams.append('event_types', params.event_types);
    if (params?.severity) queryParams.append('severity', params.severity);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/auth/organizations/${organizationId}/activity-log/export?${queryString}`
      : `/auth/organizations/${organizationId}/activity-log/export`;

    const response = await fetch(`${API_BASE}${url}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
    });

    if (!response.ok) {
      throw new Error('Failed to export activity log');
    }
    return await response.blob();
  },

  formatSeverity(severity: string): string {
    const map: Record<string, string> = {
      info: 'Info', low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical',
    };
    return map[severity] || severity;
  },

  getSeverityColor(severity: string): string {
    const map: Record<string, string> = {
      info: '#3b82f6', low: '#22c55e', medium: '#f59e0b', high: '#f97316', critical: '#dc2626',
    };
    return map[severity] || '#6b7280';
  },

  formatAuditAction(action: string): string {
    return action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  },

  getRiskScoreColor(score: number): string {
    if (score >= 80) return '#dc2626';
    if (score >= 60) return '#f97316';
    if (score >= 40) return '#f59e0b';
    return '#22c55e';
  },

  isRecentEvent(timestamp: string, hoursThreshold: number = 24): boolean {
    const diffHours = (Date.now() - new Date(timestamp).getTime()) / 3_600_000;
    return diffHours <= hoursThreshold;
  },
};
